import { Grid, Typography, Button, Box } from "@mui/material";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { isFieldDiff } from "../../../../../types/review";
import FieldDiff from "./FieldDiff";

/**
 * A location, either as plain values (a creation request) or as a per-field
 * {old, new} diff (an edit).
 *
 * This component had no diff support at all: it read `location.street`
 * directly, so once the backend started recording location as a diff it would
 * have rendered the diff OBJECT — "[object Object]" — instead of the address.
 * Every other field showed a before/after and this one silently showed only
 * the proposal, which is the one field where "where was it before?" matters
 * most to a reviewer.
 */

type MaybeDiff = string | number | { old: any; new: any } | undefined;

interface LocationDetailsProps {
  location: {
    latitude?: MaybeDiff;
    longitude?: MaybeDiff;
    street?: MaybeDiff;
    streetName?: MaybeDiff;
    description?: MaybeDiff;
  };
  onViewMap?: () => void;
}

// The map button needs a concrete coordinate, so resolve a diff to its
// proposed value — the map should show where the car wash is moving TO.
const resolve = (value: MaybeDiff) =>
  isFieldDiff(value) ? (value as any).new : value;

const LocationDetails = ({ location, onViewMap }: LocationDetailsProps) => {
  const { t } = useTranslation();

  const streetValue = location?.street ?? location?.streetName;
  const latValue = location?.latitude;
  const lonValue = location?.longitude;

  const renderValue = (value: MaybeDiff, fallback = "N/A") => {
    if (isFieldDiff(value)) {
      return (
        <FieldDiff
          diff={value as any}
          formatValue={(v) =>
            v === null || v === undefined || v === "" ? "—" : String(v)
          }
        />
      );
    }
    return (
      <Typography variant="body1">
        {value === null || value === undefined || value === ""
          ? fallback
          : String(value)}
      </Typography>
    );
  };

  const hasCoordinateDiff = isFieldDiff(latValue) || isFieldDiff(lonValue);

  return (
    <Grid size={12}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="body2" color="text.secondary">
          {t("carWashDetails.location")}
        </Typography>
        {onViewMap && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LocationOnIcon />}
            onClick={onViewMap}
          >
            {t("carWashDetails.viewOnMap")}
          </Button>
        )}
      </Box>

      {renderValue(streetValue)}

      {location?.description !== undefined && location?.description !== "" && (
        <Box mt={0.5}>{renderValue(location.description, "")}</Box>
      )}

      {/* Coordinates only get the old→new treatment when they actually moved;
          otherwise they stay a quiet one-line caption rather than shouting a
          change that did not happen. */}
      {hasCoordinateDiff ? (
        <Box mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t("carWashDetails.coordinates")}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {renderValue(latValue)}
            {renderValue(lonValue)}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("carWashDetails.coordinates")} {resolve(latValue) ?? "N/A"},{" "}
          {resolve(lonValue) ?? "N/A"}
        </Typography>
      )}
    </Grid>
  );
};

export default LocationDetails;
